import { Fragment, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { CheckCircle2, ChevronDown, ChevronRight, Copy, Download, Gift, Loader2, Search } from 'lucide-react';
import { catalog, redemptions, type RedemptionGroup } from '../api/index.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import {
  codesOnlyCsv,
  downloadCsv,
  mergeRedemptionCsv,
  redemptionCodesFromCsv,
  redemptionGroupCsv,
} from '../lib/redemptionCsv.js';

type ProductType = 'item' | 'group';
const CHUNK_SIZE = 500;
const MAX_CODES = 10_000;

interface GenerationResult {
  csv: string;
  codes: string[];
  filename: string;
  logicalName: string;
  chunkNames: string[];
  requestedCount: number;
}

function nextAvailableName(base: string, existingNames: string[]): string {
  const existing = existingNames.map((name) => name.toLowerCase());
  const isUsed = (candidate: string) => {
    const normalized = candidate.toLowerCase();
    return existing.some((name) => name === normalized || name.startsWith(`${normalized} [`));
  };
  if (!isUsed(base)) return base;
  for (let suffix = 2; suffix < 10_000; suffix += 1) {
    const candidate = `${base} (${suffix})`;
    if (!isUsed(candidate)) return candidate;
  }
  return `${base} ${Date.now()}`;
}

function safeFilename(value: string): string {
  return value.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '') || 'redemption-codes';
}

export function Redemptions() {
  const queryClient = useQueryClient();
  const [productType, setProductType] = useState<ProductType>('item');
  const [productId, setProductId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [redemptionName, setRedemptionName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [generationResult, setGenerationResult] = useState<GenerationResult | null>(null);
  const [generationError, setGenerationError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [chunkProgress, setChunkProgress] = useState({ completed: 0, total: 0 });
  const [copyNotice, setCopyNotice] = useState('');
  const [historyNotice, setHistoryNotice] = useState('');
  const [expandedGroup, setExpandedGroup] = useState('');

  const { data: seriesData } = useQuery({
    queryKey: ['catalog-series'],
    queryFn: catalog.series,
    staleTime: 10 * 60 * 1000,
  });
  const seriesNameById = useMemo(
    () => new Map((seriesData?.series ?? []).map((series) => [series._id, series.name])),
    [seriesData],
  );

  const { data: itemData, isFetching: itemsLoading } = useQuery({
    queryKey: ['redemption-items', productSearch],
    queryFn: () => catalog.items({ search: productSearch || undefined, limit: 200, sortBy: 'name' }),
    enabled: productType === 'item',
    staleTime: 5 * 60 * 1000,
  });
  const { data: groupData, isFetching: groupsLoading } = useQuery({
    queryKey: ['redemption-groups', productSearch],
    queryFn: () => catalog.groups(undefined, productSearch || undefined),
    enabled: productType === 'group',
    staleTime: 5 * 60 * 1000,
  });

  const productOptions = useMemo(() => {
    if (productType === 'item') {
      return (itemData?.items ?? []).map((item) => ({
        value: item._id,
        label: `${item.name}${item.seriesId ? ` — ${seriesNameById.get(item.seriesId) ?? 'Unknown series'}` : ''}`,
      }));
    }
    return (groupData?.groups ?? []).map((group) => {
      const seriesName = group.seriesName || (group.seriesId ? seriesNameById.get(group.seriesId) : '');
      return {
        value: group._id,
        label: seriesName ? `${seriesName} — ${group.name}` : group.name,
      };
    });
  }, [groupData, itemData, productType, seriesNameById]);

  const listQuery = useQuery({
    queryKey: ['redemptions', page, search],
    queryFn: () => redemptions.list(page, search),
  });

  const selectProductType = (next: ProductType) => {
    setProductType(next);
    setProductId('');
    setProductSearch('');
    setRedemptionName('');
    setGenerationResult(null);
    setGenerationError('');
  };

  const selectProduct = (id: string) => {
    setProductId(id);
    const product = productType === 'item'
      ? itemData?.items.find((item) => item._id === id)
      : groupData?.groups.find((group) => group._id === id);
    if (product?.name) {
      const seriesName = product.seriesId ? seriesNameById.get(product.seriesId) : undefined;
      const productLabel = seriesName ? `${seriesName} — ${product.name}` : product.name;
      const base = `${productLabel} ${format(new Date(), 'MM-dd-yy')}`;
      setRedemptionName(nextAvailableName(base, listQuery.data?.redemptionNames ?? []));
    }
  };

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const submitCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (generating) return;

    const totalChunks = Math.ceil(quantity / CHUNK_SIZE);
    const logicalName = nextAvailableName(
      redemptionName.trim(),
      listQuery.data?.redemptionNames ?? [],
    );
    setRedemptionName(logicalName);
    const chunkNames = Array.from({ length: totalChunks }, (_, index) =>
      totalChunks === 1 ? logicalName : `${logicalName} [${index + 1}/${totalChunks}]`,
    );
    const csvParts: string[] = [];

    setGenerating(true);
    setGenerationError('');
    setGenerationResult(null);
    setCopyNotice('');
    setChunkProgress({ completed: 0, total: totalChunks });

    try {
      for (let index = 0; index < totalChunks; index += 1) {
        const chunkQuantity = Math.min(CHUNK_SIZE, quantity - index * CHUNK_SIZE);
        const result = await redemptions.create({
          numbersToCreate: chunkQuantity,
          redemptionName: chunkNames[index],
          ...(productType === 'item' ? { item: productId } : { group: productId }),
        });
        csvParts.push(result.csv);
        setChunkProgress({ completed: index + 1, total: totalChunks });
      }
    } catch (error) {
      const completedCsv = mergeRedemptionCsv(csvParts);
      const completedCodes = redemptionCodesFromCsv(completedCsv);
      if (completedCodes.length > 0) {
        setGenerationResult({
          csv: completedCsv,
          codes: completedCodes,
          filename: `${safeFilename(logicalName)}.csv`,
          logicalName,
          chunkNames: chunkNames.slice(0, csvParts.length),
          requestedCount: quantity,
        });
      }
      const detail = error instanceof Error ? error.message : 'Generation failed';
      setGenerationError(
        csvParts.length > 0
          ? `Stopped after ${completedCodes.length} confirmed codes. ${detail}`
          : detail,
      );
      return;
    } finally {
      setGenerating(false);
      await queryClient.invalidateQueries({ queryKey: ['redemptions'] });
    }

    const csv = mergeRedemptionCsv(csvParts);
    setGenerationResult({
      csv,
      codes: redemptionCodesFromCsv(csv),
      filename: `${safeFilename(logicalName)}.csv`,
      logicalName,
      chunkNames,
      requestedCount: quantity,
    });
  };

  const copyToClipboard = async (value: string, notice: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyNotice(notice);
    } catch {
      setCopyNotice('Copy failed. Use Download CSV.');
    }
  };

  const csvForGroup = (group: RedemptionGroup) => redemptionGroupCsv(
    group.name,
    group.productName,
    group.seriesId ? (seriesNameById.get(group.seriesId) ?? '') : '',
    group.codes.map((code) => code.code),
  );

  const copyHistoryToClipboard = async (value: string, notice: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setHistoryNotice(notice);
    } catch {
      setHistoryNotice('Copy failed. Use Download CSV.');
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Gift size={22} className="text-brand-600" /> Redemption Codes
        </h1>
        <p className="text-gray-500 text-sm mt-1">Generate item or grouping codes and review codes stored in MongoDB.</p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Generate codes</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="flex gap-2">
              <Button type="button" variant={productType === 'item' ? 'primary' : 'secondary'} onClick={() => selectProductType('item')}>
                Item
              </Button>
              <Button type="button" variant={productType === 'group' ? 'primary' : 'secondary'} onClick={() => selectProductType('group')}>
                Grouping
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(18rem,2fr)_minmax(14rem,1fr)_8rem] gap-4">
              <label className="text-sm font-medium text-gray-700">
                {productType === 'item' ? 'Item' : 'Grouping'}
                <input
                  value={productSearch}
                  onChange={(event) => setProductSearch(event.target.value)}
                  placeholder={`Filter ${productType === 'item' ? 'items' : 'groupings'}…`}
                  className="mt-1 mb-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <SearchableSelect
                  value={productId}
                  onChange={selectProduct}
                  options={productOptions}
                  placeholder={(itemsLoading || groupsLoading) ? 'Loading…' : `Select ${productType}…`}
                  allowClear={false}
                  disabled={itemsLoading || groupsLoading}
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Redemption name
                <input
                  value={redemptionName}
                  onChange={(event) => setRedemptionName(event.target.value)}
                  placeholder="LDAS 09-28-26"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
              </label>

              <label className="text-sm font-medium text-gray-700">
                Quantity
                <input
                  type="number"
                  min={1}
                  max={MAX_CODES}
                  value={quantity}
                  onChange={(event) => setQuantity(Number(event.target.value))}
                  onFocus={(event) => event.currentTarget.select()}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
              </label>
            </div>

            {generationError && (
              <p className="text-sm text-red-600">{generationError}</p>
            )}
            {generating && (
              <p className="flex items-center gap-2 text-sm text-brand-700">
                <Loader2 size={16} className="animate-spin" />
                Creating batch {Math.min(chunkProgress.completed + 1, chunkProgress.total)} of {chunkProgress.total}…
              </p>
            )}
            <Button
              type="submit"
              loading={generating}
              disabled={!productId || !redemptionName.trim() || quantity < 1 || quantity > MAX_CODES}
            >
              <Gift size={16} /> Generate codes
            </Button>
          </form>

          {generationResult && (
            <div className={`mt-5 rounded-lg border p-4 ${
              generationResult.codes.length === generationResult.requestedCount
                ? 'border-green-200 bg-green-50'
                : 'border-amber-200 bg-amber-50'
            }`}>
              <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <CheckCircle2 size={16} className="text-green-600" />
                {generationResult.codes.length} of {generationResult.requestedCount} codes ready
              </p>
              <p className="mt-1 text-xs text-gray-600">
                {generationResult.logicalName}
                {generationResult.chunkNames.length > 1
                  ? ` · ${generationResult.chunkNames.length} automatic batches`
                  : ''}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => downloadCsv(generationResult.csv, generationResult.filename)}
                >
                  <Download size={14} /> Download full CSV
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => downloadCsv(
                    codesOnlyCsv(generationResult.codes),
                    `${safeFilename(generationResult.logicalName)}-codes.csv`,
                  )}
                >
                  <Download size={14} /> Download codes only
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => copyToClipboard(generationResult.csv.replace(/^\uFEFF/, ''), 'Full CSV copied.')}
                >
                  <Copy size={14} /> Copy full CSV
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => copyToClipboard(
                    codesOnlyCsv(generationResult.codes).replace(/^\uFEFF/, ''),
                    'Code-only CSV copied.',
                  )}
                >
                  <Copy size={14} /> Copy codes as CSV
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => copyToClipboard(`${generationResult.codes.join('\n')}\n`, 'Codes copied as text.')}
                >
                  <Copy size={14} /> Copy codes as text
                </Button>
              </div>
              {copyNotice && <p className="mt-2 text-xs text-gray-600">{copyNotice}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Generated codes</CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              {listQuery.data?.total ?? 0} redemption groups · {listQuery.data?.totalCodes ?? 0} codes
            </p>
          </div>
          <form onSubmit={submitSearch} className="flex gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search codes, names, products…"
                className="w-72 pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <Button type="submit" variant="secondary">Search</Button>
          </form>
        </CardHeader>

        {listQuery.isLoading ? (
          <div className="py-16 flex justify-center text-gray-400"><Loader2 className="animate-spin mr-2" /> Loading codes…</div>
        ) : listQuery.error ? (
          <p className="py-12 text-center text-sm text-red-600">
            {listQuery.error instanceof Error ? listQuery.error.message : 'Could not load codes'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="w-8 px-2 py-3" aria-label="Expand" />
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redemption name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Series</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redeemed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(listQuery.data?.groups ?? []).map((group) => {
                    const expanded = expandedGroup === group.name;
                    const groupCodes = group.codes.map((code) => code.code);
                    const groupCsv = csvForGroup(group);
                    return (
                      <Fragment key={group.name}>
                        <tr className="hover:bg-gray-50">
                          <td className="pl-3 py-3">
                            <button
                              type="button"
                              onClick={() => setExpandedGroup(expanded ? '' : group.name)}
                              className="p-1 text-gray-400 hover:text-gray-700"
                              aria-label={expanded ? 'Collapse codes' : 'Expand codes'}
                            >
                              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{group.name}</td>
                          <td className="px-4 py-3 text-gray-900">
                            {group.productName || '—'}
                            {group.productType && <span className="ml-2 text-[11px] text-gray-400">{group.productType}</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-500">
                            {group.seriesId ? (seriesNameById.get(group.seriesId) ?? '—') : '—'}
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-900">{group.codeCount}</td>
                          <td className="px-4 py-3 text-gray-700">{group.redeemedCount} / {group.codeCount}</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {group.createdAt ? format(new Date(group.createdAt), 'MMM d, yyyy') : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => downloadCsv(groupCsv, `${safeFilename(group.name)}.csv`)}
                              >
                                <Download size={13} /> CSV
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => downloadCsv(
                                  codesOnlyCsv(groupCodes),
                                  `${safeFilename(group.name)}-codes.csv`,
                                )}
                              >
                                <Download size={13} /> Code only
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => copyHistoryToClipboard(
                                  groupCsv.replace(/^\uFEFF/, ''),
                                  `${group.name} CSV copied.`,
                                )}
                              >
                                <Copy size={13} /> CSV
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => copyHistoryToClipboard(
                                  `${groupCodes.join('\n')}\n`,
                                  `${group.codeCount} codes copied.`,
                                )}
                              >
                                <Copy size={13} /> Codes
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr key={`${group.name}-codes`}>
                            <td colSpan={8} className="bg-gray-50 px-12 py-3">
                              <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                <table className="w-full text-xs">
                                  <thead className="sticky top-0 bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-medium text-gray-500">Code</th>
                                      <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                                      <th className="px-3 py-2 text-left font-medium text-gray-500">Created</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {group.codes.map((code) => (
                                      <tr key={code._id}>
                                        <td className="px-3 py-2 font-mono font-semibold text-gray-900">{code.code || '—'}</td>
                                        <td className="px-3 py-2">
                                          {code.status ? <Badge variant={statusBadge(code.status)}>{code.status}</Badge> : '—'}
                                        </td>
                                        <td className="px-3 py-2 text-gray-500">
                                          {code.createdAt ? format(new Date(code.createdAt), 'MMM d, yyyy h:mm a') : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {(listQuery.data?.groups.length ?? 0) === 0 && (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">No generated code groups found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {historyNotice && (
              <p className="px-4 py-2 border-t border-gray-100 text-xs text-gray-600">{historyNotice}</p>
            )}
            {(listQuery.data?.pages ?? 1) > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">Page {page} of {listQuery.data?.pages}</span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
                  <Button variant="secondary" size="sm" disabled={page >= (listQuery.data?.pages ?? 1)} onClick={() => setPage((value) => value + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
